import { useRef, useState, useEffect } from 'react'
import { DndContext, rectIntersection, DragEndEvent, DragOverlay, useDraggable } from '@dnd-kit/core'
import { AnimatePresence, motion } from 'framer-motion'
import Toast from '@/components/interAction/Toast'
import FlipCard from '@/components/interAction/FlipCard';
import * as styles from '@/components/organisms/Learning/LearningSytle.css'
import { DraggableImage } from '@/components/interAction/Drag/DraggableImage'
import { TargetWord } from '@/components/interAction/Drag/TargetWord'
import { AnimatedLine } from '@/components/interAction/Line'
import { getStudentActivityTitle } from '@/utils/activityTitle';
import clsx from 'clsx';

// 토스트 상태
type ToastState = {
    id: number | null
    type: 'success' | 'error' | null
  }

// 드래그 가능한 텍스트 컴포넌트
function DraggableText({ id, text, disabled }: { id: string; text: string; disabled?: boolean }) {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id,
    disabled,
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className="flex justify-center items-center px-4 py-3 text-center bg-white rounded-[16px] border border-gray-100 cursor-move lg:size-24 size-20"
      style={{
        transition: 'all 100.0s linear',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'default' : 'move',
        pointerEvents: disabled ? 'none' : 'auto',
        touchAction: 'none', // 모바일 터치 드래그 지원
        WebkitTouchCallout: 'none', // iOS 롱프레스 메뉴 비활성화
        userSelect: 'none', // 텍스트 선택 방지
      }}
    >
      <p className="text-sm font-medium text-gray-700">{text}</p>
    </div>
  );
}

// 드래그 가능한 점 컴포넌트
function DraggableDot({ 
  id, 
  disabled, 
  status, 
  dotRef 
}: { 
  id: string; 
  disabled?: boolean; 
  status: 'correct' | 'default';
  dotRef: (el: HTMLSpanElement | null) => void;
}) {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `dot-${id}`, // 점임을 구분하기 위해 접두사 추가
    disabled,
    data: { type: 'dot', originalId: id },
  });

  return (
    <span
      ref={(el) => {
        setNodeRef(el);
        dotRef(el);
      }}
      {...attributes}
      {...listeners}
      className={styles.dot({ status })}
      style={{
        cursor: disabled ? 'default' : 'grab',
        pointerEvents: disabled ? 'none' : 'auto',
        touchAction: 'none', // 모바일 터치 드래그 지원
        WebkitTouchCallout: 'none', // iOS 롱프레스 메뉴 비활성화
        userSelect: 'none', // 텍스트 선택 방지
      }}
    />
  );
}

interface DragMatchProps {
  activity?: any;
  unitData?: any;
  onResponse?: (response: any) => void;
  existingResponse?: any;
  isHomework?: boolean;
}

export default function DragMatch({ activity, unitData, onResponse, existingResponse, isHomework = false }: DragMatchProps) {
  const emptyStateTitle = getStudentActivityTitle(activity?.title, '드래그 매치');
  const displayTitle = getStudentActivityTitle(activity?.title, '드래그 매치를 해주세요!');

  // activity.items에서 실제 데이터 가져오기
  const activityData = activity?.items?.[0] || {};
  let flashCards: any[] = [];
  
  try {
    const contentJson = activityData.contentJson || '{}';
    const parsedData = typeof contentJson === 'string' ? JSON.parse(contentJson) : contentJson;
    flashCards = parsedData.flashCards || [];
    
    console.log('=== 드래그매치 데이터 ===');
    console.log('플래시카드 개수:', flashCards.length);
  } catch (e) {
    console.error('드래그매치 데이터 파싱 에러:', e);
  }

  // unitType 확인 (ESSAY/TRANSLATION은 이미지 없이 해석→단어)
  const unitType = unitData?.unitType || '';
  const isTextOnly = unitType === 'ESSAY' || unitType === 'TRANSLATION';
  const showMeaningUnderImage = unitType === 'STARTER_STRUCTURE';
  
  console.log('Unit Type:', unitType);
  console.log('Text Only Mode:', isTextOnly);

  // 배열 섞기 함수
  const shuffleArray = <T,>(array: T[]): T[] => {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  // flashCards 데이터를 imageList/meaningList와 wordList 형식으로 변환
  const baseImageList = flashCards.map((card, index) => {
    const content = typeof card.contentJson === 'string' ? JSON.parse(card.contentJson) : card.contentJson;
    return {
      id: `item-${index}`,
      img: content.wordImageUrl || content.imageUrl,
      word: content.word || '',
      meaning: content.meaning || '' // 해석 추가
    };
  });

  const baseWordList = flashCards.map((card, index) => {
    const content = typeof card.contentJson === 'string' ? JSON.parse(card.contentJson) : card.contentJson;
    return {
      id: `item-${index}`,
      label: content.word || '',
      meaning: content.meaning || ''
    };
  });

  // existingResponse가 있으면 순서 유지, 없으면 랜덤 섞기
  const imageList = existingResponse?.shuffledImageOrder 
    ? existingResponse.shuffledImageOrder.map((id: string) => baseImageList.find(item => item.id === id)!).filter(Boolean)
    : shuffleArray(baseImageList);
    
  const wordList = existingResponse?.shuffledWordOrder
    ? existingResponse.shuffledWordOrder.map((id: string) => baseWordList.find(item => item.id === id)!).filter(Boolean)
    : shuffleArray(baseWordList);
  
  const rowCount = Math.max(imageList.length, wordList.length);
  
  // existingResponse에서 이전 매칭 정보 복원
  const initialMatched = existingResponse?.matched || {};
  const initialLines = existingResponse?.lines || [];
  
  const [activeId, setActiveId] = useState<string | null>(null)
  const [activeType, setActiveType] = useState<'dot' | 'image' | null>(null)
  const [dragStartPosition, setDragStartPosition] = useState<{ x: number; y: number } | null>(null)
  const [matched, setMatched] = useState<Record<string, boolean | string>>(initialMatched)
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  // 토스트 노출 상태
  const [toast, setToast] = useState<ToastState>({ id: null, type: null })
  const [flipStatus, setFlipStatus] = useState<'idle' | 'success'>('idle');

  const handleImageError = (id: string) => {
    setFailedImages((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  // 초기 응답 설정
  useEffect(() => {
    if (onResponse && !existingResponse) {
      onResponse({
        matched: {},
        lines: [],
        matchedPairs: [],
        shuffledImageOrder: imageList.map((item: any) => item.id),
        shuffledWordOrder: wordList.map((item: any) => item.id),
        isCorrect: false,
        isHomeworkReady: false
      });
    } else if (onResponse && existingResponse) {
      // 기존 응답이 있을 때 isCorrect 재계산
      const allMatched = wordList.every((word: any) => initialMatched[word.id]);
      const allCorrect = allMatched && wordList.every((word: any) => {
        const matchValue = initialMatched[word.id];
        return matchValue === true || matchValue === word.id;
      });
      
      onResponse({
        ...existingResponse,
        shuffledImageOrder: imageList.map((item: any) => item.id),
        shuffledWordOrder: wordList.map((item: any) => item.id),
        isCorrect: allCorrect,
        isHomeworkReady: allMatched
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  //드래그 시작
  const handleDragStart = (event: any) => {
    const active = event.active;
    const id = active.id;
    const data = active.data?.current;
    
    // 점인지 이미지인지 판단
    if (data?.type === 'dot') {
      // 점인 경우 원본 id 사용
      setActiveId(data.originalId);
      setActiveType('dot');
      // 점의 위치 저장
      const dotElement = fromRefs.current[data.originalId];
      if (dotElement) {
        const rect = dotElement.getBoundingClientRect();
        setDragStartPosition({ x: rect.left, y: rect.top });
      }
    } else {
      // 이미지/텍스트인 경우
      setActiveId(id);
      setActiveType('image');
      // 이미지의 위치 저장
      const imageElement = imageRefs.current[id];
      if (imageElement) {
        const rect = imageElement.getBoundingClientRect();
        setDragStartPosition({ x: rect.left, y: rect.top });
      } else {
        // 텍스트 요소인 경우
        requestAnimationFrame(() => {
          const container = containerRef.current;
          if (container) {
            const textElements = container.querySelectorAll('[class*="flex justify-center items-center px-4 py-3"]');
            for (const textEl of textElements) {
              if (textEl.textContent?.includes(imageList.find((item: any) => item.id === id)?.meaning || '')) {
                const rect = textEl.getBoundingClientRect();
                setDragStartPosition({ x: rect.left, y: rect.top });
                break;
              }
            }
          }
        });
      }
    }
  }
    const containerRef = useRef<HTMLDivElement | null>(null);
    // 드래그 매치 후 시작 선
    const fromRefs = useRef<Record<string, HTMLSpanElement | null>>({})
    // 드래그 매치 후 도착 선
    const toRefs = useRef<Record<string, HTMLSpanElement | null>>({})
    // 이미지 요소 ref 저장
    const imageRefs = useRef<Record<string, HTMLDivElement | null>>({})
    // 선 연결 타입
    type LineInfo = {
      id: string;
      fromId: string;
      toId: string;
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      status: 'correct' | 'incorrect';
    };
    const [lines, setLines] = useState<LineInfo[]>(
      (initialLines || []).map((line: any) => ({
        ...line,
        fromId: String(line.fromId || line.id.split('-')[0] || ''),
        toId: String(line.toId || line.id.split('-')[1] || ''),
        x1: 0,
        y1: 0,
        x2: 0,
        y2: 0,
      }))
    );

    const computeLinePosition = (fromId: string, toId: string) => {
      const container = containerRef.current;
      if (!container) return null;
      const from = fromRefs.current[fromId];
      const to = toRefs.current[toId];
      if (!from || !to) return null;
      const containerRect = container.getBoundingClientRect();
      const fromRect = from.getBoundingClientRect();
      const toRect = to.getBoundingClientRect();
      return {
        x1: fromRect.left - containerRect.left + fromRect.width / 2,
        y1: fromRect.top - containerRect.top + fromRect.height / 2,
        x2: toRect.left - containerRect.left + toRect.width / 2,
        y2: toRect.top - containerRect.top + toRect.height / 2,
      };
    };

    const syncLinePositions = () => {
      setLines((prev) =>
        prev.map((line) => {
          const coords = computeLinePosition(line.fromId, line.toId);
          return coords ? { ...line, ...coords } : line;
        })
      );
    };

    useEffect(() => {
      const handleResize = () => {
        syncLinePositions();
      };
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);
    
    // 컴포넌트 마운트 시 이전 매칭 상태에 따라 선 다시 그리기
    useEffect(() => {
      if (Object.keys(initialMatched).length > 0) {
        console.log('=== DragMatch: 이전 매칭 상태 복원 ===');
        console.log('initialMatched:', initialMatched);
        console.log('isHomework:', isHomework);
        
        // ref가 설정될 때까지 대기
        setTimeout(() => {
          const newLines: typeof lines = [];
          
          Object.keys(initialMatched).forEach((toId) => {
            const matchValue = initialMatched[toId];
            if (matchValue) {
              // 학습 모드: matched[id] = true, id가 from과 to 모두 같음
              // 숙제 모드: matched[toId] = fromId, 다를 수 있음
              const fromId = typeof matchValue === 'string' ? matchValue : toId;
              
              console.log(`매칭 복원: from=${fromId} → to=${toId}`);
              
              const from = fromRefs.current[fromId];
              const to = toRefs.current[toId];
              
              if (from && to) {
                const coords = computeLinePosition(fromId, toId);
                if (coords) {
                  newLines.push({
                    id: `${fromId}-${toId}`,
                    fromId,
                    toId,
                    ...coords,
                    status: 'correct',
                  });
                }
              } else {
                console.log(`⚠️ ref를 찾을 수 없습니다: from=${fromId}, to=${toId}`);
              }
            }
          });
          
          console.log(`총 ${newLines.length}개의 선 복원됨`);
          
          if (newLines.length > 0) {
            setLines(newLines);
            requestAnimationFrame(() => syncLinePositions());
          }
        }, 100);
      }
    }, []); // 마운트 시 한 번만 실행

    // 드래그 끝
const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
  
    const toastId = Date.now()
    
    // over가 없으면 아무것도 안 함
    if (!over) {
      setActiveId(null);
      setActiveType(null);
      setDragStartPosition(null);
      return;
    }
    
    // 점인 경우 원본 id 추출
    const activeData = active.data?.current;
    const activeOriginalId = activeData?.type === 'dot' ? activeData.originalId : String(active.id);
    const overId = String(over.id);
    
    // 정답 여부 확인 (원본 id로 비교)
    const isCorrect = activeOriginalId === overId;
    
    // 학습 모드에서 틀린 경우: 빨간색 선만 일시적으로 표시
    if (!isHomework && !isCorrect) {
        const from = fromRefs.current[activeOriginalId];
        const to = toRefs.current[overId];
        
        if (from && to) {
          requestAnimationFrame(() => {
            const coords = computeLinePosition(activeOriginalId, overId);
            if (!coords) return;
            const incorrectLine: LineInfo = {
              id: `temp-${activeOriginalId}-${overId}-${Date.now()}`,
              fromId: activeOriginalId,
              toId: overId,
              ...coords,
              status: 'incorrect' as const,
            };
            
            setLines((prev) => [...prev, incorrectLine]);
            requestAnimationFrame(() => syncLinePositions());
            
            // 1초 후 틀린 선 제거
            setTimeout(() => {
              setLines((prev) => prev.filter(line => line.id !== incorrectLine.id));
            }, 1000);
          });
        }
        
        // ❌ 오답 토스트
        setToast({ id: toastId, type: 'error' });
        
        setTimeout(() => {
          setToast((prev) => (prev.id === toastId ? { id: null, type: null } : prev));
        }, 2000);
      
        setActiveId(null);
        setActiveType(null);
        setDragStartPosition(null);
        return;
    }
    
    // 숙제 모드 또는 학습 모드 정답인 경우: 매칭 허용
    if (isHomework || isCorrect) {
        const targetId = overId;
        const draggedId = activeOriginalId;

        // 매칭 상태 업데이트 (숙제는 toId -> fromId 저장, 학습은 정답 여부만 저장)
        const updatedMatched = { ...matched };

        // 숙제 모드에서는 1:1 매칭을 위해 기존에 동일한 이미지/텍스트와 연결된 선을 제거
        Object.keys(updatedMatched).forEach((key) => {
          if (updatedMatched[key] === draggedId || key === targetId) {
            delete updatedMatched[key];
          }
        });

        const matchKey = targetId;
        const matchValue = isHomework ? draggedId : true;
        updatedMatched[matchKey] = matchValue;
        
        setMatched(updatedMatched);
      
        // ✅ 전체 정답 체크 (학습 모드에서만)
        if (!isHomework) {
          const allMatched = wordList.every((word: any) => updatedMatched[word.id] === true);
          if (allMatched) {
            setFlipStatus('success');
      
            // ✅ 2초 후 원상 복귀
            setTimeout(() => {
              setFlipStatus('idle');
            }, 2000);
          }
        }
  
        const from = fromRefs.current[draggedId];
        const to = toRefs.current[targetId];
        
        if (from && to) {
          requestAnimationFrame(() => {
            const coords = computeLinePosition(draggedId, targetId);
            if (!coords) return;
            const lineStatus: 'correct' | 'incorrect' = isHomework ? 'correct' : (isCorrect ? 'correct' : 'incorrect');
            
            const newLine: LineInfo = {
              id: `${draggedId}-${targetId}`,
              fromId: draggedId,
              toId: targetId,
              ...coords,
              status: lineStatus,
            };
            
            setLines((prev) => {
              // 기존 선 중 동일한 시작/도착 지점을 가진 선 제거 (1:1 보장)
              const filteredLines = prev.filter(line => {
                return line.fromId !== draggedId && line.toId !== targetId;
              });
              const updatedLines = [...filteredLines, newLine];
              
              // 모든 매칭 쌍 정보 수집
              const matchedPairs = Object.keys(updatedMatched)
                .filter(id => updatedMatched[id])
                .map(id => {
                  const matchedId = isHomework ? updatedMatched[id] : id;
                  const img = imageList.find((i: any) => i.id === matchedId);
                  const word = wordList.find((w: any) => w.id === id);
                  return {
                    fromId: String(matchedId),
                    toId: String(id),
                    word: img?.word || '',
                    meaning: word?.meaning || '',
                    isCorrect: isHomework ? (matchedId === id) : true
                  };
                });
              
              // 모든 아이템이 매칭되었는지 체크
              const allMatched = wordList.every((word: any) => updatedMatched[word.id]);
              
              // 모든 매칭이 정답인지 체크 (학습 모드에서만)
              const allCorrect = allMatched && wordList.every((word: any) => {
                const matchValue = updatedMatched[word.id];
                return matchValue === true || matchValue === word.id;
              });
              
              // 매칭 상태 저장
              if (onResponse) {
              onResponse({ 
                matched: updatedMatched,
                lines: updatedLines,
                matchedPairs: matchedPairs,
                shuffledImageOrder: imageList.map((item: any) => item.id),
                shuffledWordOrder: wordList.map((item: any) => item.id),
                isCorrect: allCorrect,
                isHomeworkReady: allMatched
              });
              }
              
              return updatedLines;
            });
            requestAnimationFrame(() => syncLinePositions());
          });
        }
  
        // ✅ 정답 토스트 (학습 모드에서만 표시)
        if (!isHomework && isCorrect) {
          setToast({ id: toastId, type: 'success' });
          
          setTimeout(() => {
            setToast((prev) => (prev.id === toastId ? { id: null, type: null } : prev));
          }, 2000);
        }
    }
  
    setActiveId(null);
    setActiveType(null);
    setDragStartPosition(null);
  }

  // 데이터가 없는 경우 처리
  if (flashCards.length === 0) {
    return (
      <main className="p-5">
        <h1 className="mb-5 heading1">{emptyStateTitle}</h1>
        <p className="text-center text-gray-500">플래시카드 데이터가 없습니다.</p>
      </main>
    );
  }

  return (
    <main className="p-5">
      <div 
        ref={containerRef} 
        className="relative"
        style={{
          touchAction: 'pan-y', // 세로 스크롤은 허용하되 드래그는 우선 처리
        }}
      >
      <h1 className="mb-5 heading1">{displayTitle}</h1>
      <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd} collisionDetection={rectIntersection}>
        <FlipCard
            flipStatus={flipStatus}
            front={
                <div className={styles.gameCard}>
                    <div className="flex flex-col gap-4 w-full">
                        {Array.from({ length: rowCount }).map((_, index) => {
                        const imageItem = imageList[index];
                        const wordItem = wordList[index];
                        const rowKey = `drag-match-row-${imageItem?.id ?? ''}-${wordItem?.id ?? index}`;
                        
                        return (
                            <div key={rowKey} className="flex gap-6 justify-between items-stretch w-full">
                            <div className={styles.listItem}>
                                {imageItem ? (
                                isTextOnly ? (
                                    <DraggableText
                                    id={imageItem.id}
                                    text={imageItem.meaning}
                                    disabled={!isHomework && Boolean(matched[imageItem.id])}
                                    />
                                ) : (
                                    <div 
                                    className="flex flex-col gap-2 items-center"
                                    ref={(el) => { imageRefs.current[imageItem.id] = el }}
                                    >
                                    <DraggableImage
                                        id={imageItem.id}
                                        img={imageItem.img}
                                        disabled={!isHomework && Boolean(matched[imageItem.id])}
                                        isError={failedImages.has(imageItem.id)}
                                        onError={() => handleImageError(imageItem.id)}
                                    />
                                    {showMeaningUnderImage && imageItem.meaning && (
                                        <p className="text-sm font-medium text-gray-600 text-center max-w-[160px]">
                                        {imageItem.meaning}
                                        </p>
                                    )}
                                    </div>
                                )
                                ) : (
                                <div className="flex flex-col gap-2 items-center">
                                    <div className={styles.imageWrapper} />
                                </div>
                                )}
                                {imageItem && (
                                <DraggableDot
                                    id={imageItem.id}
                                    disabled={!isHomework && Boolean(matched[imageItem.id])}
                                    status={(!isHomework && matched[imageItem.id]) ? 'correct' : 'default'}
                                    dotRef={(el) => { fromRefs.current[imageItem.id] = el }}
                                />
                                )}
                            </div>

                            <div className={styles.listItem}>
                                {wordItem && (
                                <span
                                    className={styles.dot({ status: (!isHomework && matched[wordItem.id]) ? 'correct' : 'default' })}
                                    ref={(el) => { toRefs.current[wordItem.id] = el }}
                                ></span>
                                )}
                                {wordItem ? (
                                <TargetWord id={wordItem.id} label={wordItem.label} isMatched={!isHomework && Boolean(matched[wordItem.id])} />
                                ) : (
                                <div className={styles.wordWrapper} />
                                )}
                            </div>
                            </div>
                        )
                        })}
                    </div>
                </div>
            }
              />
            <DragOverlay
            style={{
                transition:'all 0s',
                touchAction: 'none', // 모바일 터치 드래그 지원
                WebkitTouchCallout: 'none', // iOS 롱프레스 메뉴 비활성화
                userSelect: 'none', // 텍스트 선택 방지
            }}>
            {activeId ? (
                isTextOnly ? (
                  // 에세이/번역: 텍스트 오버레이
                  <div
                    className={clsx("flex justify-center items-center px-4 py-3 text-center bg-white border border-gray-100 rounded-[16px] lg:size-24 size-20")}
                    style={{ opacity: 0.6, pointerEvents: 'none' }}
                  >
                    <p className="text-sm font-medium text-gray-700">
                      {imageList.find((i: any) => i.id === activeId)?.meaning}
                    </p>
                  </div>
                ) : (
                  // 일반: 이미지 오버레이 (점에서 드래그해도 이미지 오버레이 표시)
                  <div
                  className={styles.imageWrapper}
                  style={{ opacity: 0.6, pointerEvents: 'none' }}
                  >
                  {imageList.find((i: any) => i.id === activeId)?.img && !failedImages.has(activeId!) ? (
                    <img src={imageList.find((i: any) => i.id === activeId)?.img} alt="" className='object-cover w-full h-full' />
                  ) : (
                    <div className="flex items-center justify-center w-full h-full bg-gray-100 text-gray-400 font-bold text-[10px] text-center leading-tight">
                        이미지가<br/>없습니다
                    </div>
                  )}
                  </div>
                )
            ) : null}
            </DragOverlay>
      </DndContext>
      <svg className="absolute top-0 left-0 z-10 w-full h-full pointer-events-none">
        {lines.map((line) => (
            <AnimatedLine 
              key={line.id} 
              x1={line.x1} 
              y1={line.y1} 
              x2={line.x2} 
              y2={line.y2}
              color={isHomework ? '#9ca3af' : (line.status === 'correct' ? '#10b981' : '#ef4444')}
            />
        ))}
      </svg>
        {!isHomework && (
          <AnimatePresence>
            {toast.id && toast.type && (
              <Toast
                key={`toast-${toast.id}`}
                type={toast.type}
                onClose={() => setToast({ id: null, type: null })}
                className="fixed top-[30%] left-1/2 z-[999] -translate-x-1/2"
              >
                <div className="flex gap-2 items-center px-5 py-3">
                  <p className="font-bold text-white">
                    {toast.type === 'success' ? '정답입니다 🎉' : '틀렸습니다 😢'}
                  </p>
                </div>
              </Toast>
            )}
          </AnimatePresence>
        )}
      </div>
    </main>
  )
}